// src/pages/PaymentDetail.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  CreditCard,
  FileText,
  User2,
  Building2,
  CalendarDays,
  DollarSign,
  ShieldCheck,
} from 'lucide-react';

import api from '../api/axios';
import { fetchPayment } from '../services/paymentService';

// ✅ shared money helpers
import { formatMoney, safeCurrency } from '../utils/money';

/* ----------------------------- helpers ----------------------------- */

const cx = (...a) => a.filter(Boolean).join(' ');

// company prefs (currency + timezone) from localStorage
const getCompanyPrefs = () => {
  try {
    const raw =
      localStorage.getItem('companyProfile') ||
      localStorage.getItem('company') ||
      localStorage.getItem('profile') || // sometimes company prefs live here
      '';
    if (!raw) return {};
    const obj = JSON.parse(raw);

    return {
      currency: obj.currency || obj.defaultCurrency || obj.baseCurrency,
      timezone: obj.timezone || obj.defaultTimezone || obj.timeZone,
    };
  } catch {
    return {};
  }
};

// ✅ safer role getter (never default to admin)
const getRole = () => {
  try {
    const p = JSON.parse(localStorage.getItem('profile') || '{}');
    const r = (p?.role || localStorage.getItem('role') || 'viewer').toLowerCase();
    return r;
  } catch {
    return (localStorage.getItem('role') || 'viewer').toLowerCase();
  }
};

const canMutateRole = () => ['admin', 'staff'].includes(getRole());

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800 border border-amber-200',
  succeeded: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  failed: 'bg-rose-100 text-rose-800 border border-rose-200',
  refunded: 'bg-blue-100 text-blue-800 border border-blue-200',
  disputed: 'bg-orange-100 text-orange-800 border border-orange-200',
  canceled: 'bg-slate-100 text-slate-700 border border-slate-200',
};

const METHOD_LABELS = {
  card: 'Card',
  cash: 'Cash',
  cheque: 'Cheque',
  bank_transfer: 'Bank Transfer',
  e_transfer: 'E-Transfer',
  other: 'Other',
};

function StatusChip({ status }) {
  const key = String(status || '').toLowerCase();
  const cls = STATUS_STYLES[key] || 'bg-slate-100 text-slate-700 border border-slate-200';
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold',
        cls
      )}
    >
      {(status || '—').toUpperCase()}
    </span>
  );
}

function MethodChip({ method }) {
  if (!method) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700 border border-slate-200">
        —
      </span>
    );
  }
  const key = String(method).toLowerCase();
  const label = METHOD_LABELS[key] || method;
  return (
    <span className="inline-flex items-center rounded-full bg-sky-50 px-2.5 py-0.5 text-xs text-sky-800 border border-sky-200">
      {label}
    </span>
  );
}

/* ----------------------------- main ----------------------------- */

export default function PaymentDetail() {
  const { id: routeId = '' } = useParams();
  const navigate = useNavigate();

  const [payment, setPayment] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  const canMutate = canMutateRole();

  // ✅ compute company prefs once per mount (safe defaults)
  const companyPrefs = useMemo(() => getCompanyPrefs(), []);
  const COMPANY_CURRENCY = useMemo(
    () => safeCurrency(companyPrefs?.currency, 'USD'),
    [companyPrefs?.currency]
  );
  const COMPANY_TZ = useMemo(() => companyPrefs?.timezone || 'UTC', [companyPrefs?.timezone]);

  // ✅ stable, crash-proof formatter with company currency fallback
  const fmtMoney = useCallback(
    (n = 0, c) => formatMoney(n, safeCurrency(c || COMPANY_CURRENCY, COMPANY_CURRENCY)),
    [COMPANY_CURRENCY]
  );

  // ✅ date-only: treat YYYY-MM-DD as date-only (do NOT let TZ shift it)
  const fmtDateOnly = useCallback(
    (value) => {
      if (!value) return '—';
      try {
        if (typeof value === 'string') {
          const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
          if (m) {
            const y = Number(m[1]);
            const mo = Number(m[2]);
            const d = Number(m[3]);
            const dt = new Date(Date.UTC(y, mo - 1, d));
            return dt.toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            });
          }
        }

        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return '—';

        // timestamps: show in company timezone
        return dt.toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          timeZone: COMPANY_TZ,
        });
      } catch {
        return '—';
      }
    },
    [COMPANY_TZ]
  );

  const fmtDateTimeLocal = useCallback(
    (value) => {
      if (!value) return '—';
      try {
        const dt = new Date(value);
        if (Number.isNaN(dt.getTime())) return '—';
        return dt.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: COMPANY_TZ,
        });
      } catch {
        return '—';
      }
    },
    [COMPANY_TZ]
  );

  const load = useCallback(async () => {
    if (!routeId) {
      setPayment(null);
      setInvoice(null);
      setClient(null);
      setLoading(false); // ✅ prevent stuck skeleton
      return;
    }

    setLoading(true);
    try {
      const raw = await fetchPayment(routeId);

      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[PaymentDetail] raw fetchPayment payload:', raw);
      }

      // Unwrap common shapes:
      const p =
        raw?.data?.data ||
        raw?.data ||
        raw?.payment ||
        raw?.result ||
        raw;

      if (!p || typeof p !== 'object') {
        setPayment(null);
        setInvoice(null);
        setClient(null);
        toast.error('Payment not found');
        return;
      }

      setPayment(p);

      const invoiceRef =
        p.invoice ||
        p.invoiceId ||
        p.invoiceRef ||
        p.invoiceSnapshot?._id ||
        null;

      const clientRef =
        p.client ||
        p.clientId ||
        p.clientRef ||
        p.clientSnapshot?._id ||
        null;

      const invoicePromise = (() => {
        if (!invoiceRef) return Promise.resolve(null);
        if (typeof invoiceRef === 'object') return Promise.resolve(invoiceRef);
        return api
          .get(`/invoices/${invoiceRef}`)
          .then((r) => r?.data?.data || r?.data || null)
          .catch(() => null);
      })();

      const clientPromise = (() => {
        if (!clientRef) return Promise.resolve(null);
        if (typeof clientRef === 'object') return Promise.resolve(clientRef);
        return api
          .get(`/clients/${clientRef}`)
          .then((r) => r?.data?.data || r?.data || null)
          .catch(() => null);
      })();

      const [inv, cli] = await Promise.all([invoicePromise, clientPromise]);
      setInvoice(inv);
      setClient(cli);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      toast.error('Failed to load payment');
      setPayment(null);
      setInvoice(null);
      setClient(null);
    } finally {
      setLoading(false);
    }
  }, [routeId]);

  useEffect(() => {
    load();
  }, [load]);

  /* ----------------------------- computed ----------------------------- */

  const currency = safeCurrency(
    payment?.currency || invoice?.currency || COMPANY_CURRENCY,
    COMPANY_CURRENCY
  );

  const amount = Number(payment?.amount ?? 0);

  // ✅ keep 0 as 0 (do not convert to null)
  const feeRaw = payment?.processorFeeAmount;
  const fee = Number.isFinite(Number(feeRaw)) ? Number(feeRaw) : null;

  const net = Number.isFinite(Number(payment?.netAmount))
    ? Number(payment.netAmount)
    : amount - (fee ?? 0);

  const isStripe = payment?.source === 'stripe';
  const stripeMeta = payment?.stripe || {};

  const paymentNumber =
    payment?.paymentNumber ||
    (payment?._id ? `PAY-${String(payment._id).slice(-6).toUpperCase()}` : 'Payment');

  const invoiceNumber =
    invoice?.invoiceNumber ||
    (invoice?._id ? `INV-${String(invoice._id).slice(-6).toUpperCase()}` : null);

  const clientName =
    client?.name ||
    client?.companyName ||
    invoice?.clientSnapshot?.name ||
    payment?.clientName ||
    '—';

  const MoneyStat = ({ label, value, muted, icon: Icon }) => (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-center gap-2 text-slate-600 text-sm">
        {Icon ? <Icon size={16} /> : null}
        {label}
      </div>
      <div
        className={cx(
          'mt-1 text-2xl font-semibold tabular-nums',
          muted ? 'text-slate-500' : 'text-slate-900'
        )}
      >
        {value != null ? fmtMoney(value, currency) : '—'}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="w-full p-6">
        <div className="animate-pulse rounded-2xl bg-slate-100 h-40 mb-6" />
        <div className="animate-pulse rounded-2xl bg-white h-[70vh] shadow" />
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="w-full min-h-[60vh] flex items-center justify-center text-slate-600">
        Payment not found.
      </div>
    );
  }

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50">
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50"
              >
                <ArrowLeft size={16} />
                Back
              </button>
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="text-slate-900 text-xl sm:text-2xl font-semibold mr-1">
                    {paymentNumber}
                  </h1>
                  <StatusChip status={payment.status} />
                  <MethodChip method={payment.method} />
                </div>
                <p className="text-slate-600 text-sm mt-1">
                  Payment details, linked invoice, and reconciliation metadata
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden sm:flex flex-col items-end">
                <span className="text-xs uppercase tracking-wide text-slate-500">
                  Amount
                </span>
                <span className="text-2xl font-semibold text-slate-900 tabular-nums">
                  {fmtMoney(amount, currency)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3 lg:grid-cols-3">
            <MoneyStat label="Gross Amount" value={amount} icon={DollarSign} />
            <MoneyStat label="Processor Fee" value={fee} muted icon={CreditCard} />
            <MoneyStat label="Net Amount" value={net} icon={DollarSign} />
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2 space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-white">
                      <CreditCard size={18} />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">Payment Summary</div>
                      <div className="mt-1 text-xs text-slate-600">
                        <span className="inline-flex items-center gap-1">
                          <CalendarDays size={14} className="text-slate-400" />
                          {payment.paymentDate
                            ? fmtDateOnly(payment.paymentDate)
                            : fmtDateOnly(payment.createdAt)}
                        </span>
                        {payment.source && (
                          <span className="ml-2 inline-flex items-center gap-1">
                            <ShieldCheck size={14} className="text-slate-400" />
                            {payment.source === 'stripe' ? 'Stripe' : 'Manual'} source
                          </span>
                        )}
                      </div>
                      {payment.reference && (
                        <div className="mt-1 text-xs text-slate-600">
                          Reference: <span className="font-medium">{payment.reference}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-4 text-sm">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-slate-500">Status</span>
                      <StatusChip status={payment.status} />
                    </div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-slate-500">Method</span>
                      <MethodChip method={payment.method} />
                    </div>
                    {payment.source && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-500">Source</span>
                        <span className="font-medium capitalize text-slate-800">
                          {payment.source}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Linked invoice & client */}
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Invoice */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-slate-900">
                      <FileText size={16} className="text-slate-500" />
                      <span className="font-semibold">Invoice</span>
                    </div>
                    {invoice?._id && (
                      <button
                        onClick={() => navigate(`/invoices/${invoice._id}`)}
                        className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                      >
                        View
                      </button>
                    )}
                  </div>

                  {invoice ? (
                    <div className="text-sm text-slate-700 space-y-1">
                      <div className="font-medium">{invoiceNumber || 'Invoice'}</div>
                      <div className="text-xs text-slate-500">
                        Issue: {fmtDateOnly(invoice.issueDate)} · Due: {fmtDateOnly(invoice.dueDate)}
                      </div>
                      <div className="flex items-center justify-between mt-2 text-sm">
                        <span className="text-slate-600">Invoice Total</span>
                        <span className="font-semibold tabular-nums">
                          {fmtMoney(invoice.totalAmount ?? 0, invoice.currency || currency)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Current Balance</span>
                        <span className="tabular-nums">
                          {fmtMoney(invoice.balanceDue ?? 0, invoice.currency || currency)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-slate-500">No invoice details available.</div>
                  )}
                </div>

                {/* Client */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-slate-900">
                      <User2 size={16} className="text-slate-500" />
                      <span className="font-semibold">Client</span>
                    </div>
                    {client?._id && (
                      <button
                        onClick={() => navigate(`/clients/${client._id}`)}
                        className="inline-flex items-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                      >
                        View
                      </button>
                    )}
                  </div>

                  <div className="text-sm text-slate-700 space-y-1">
                    <div className="font-medium">{clientName}</div>
                    {client?.companyName && (
                      <div className="flex items-center gap-1 text-xs text-slate-500">
                        <Building2 size={12} /> {client.companyName}
                      </div>
                    )}
                    {(client?.email || client?.phone) && (
                      <div className="mt-1 text-xs text-slate-600 space-y-0.5">
                        {client?.email && (
                          <a href={`mailto:${client.email}`} className="hover:underline">
                            {client.email}
                          </a>
                        )}
                        {client?.phone && (
                          <div>
                            <a href={`tel:${client.phone}`} className="hover:underline">
                              {client.phone}
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Stripe metadata */}
              {(isStripe ||
                stripeMeta.paymentIntentId ||
                stripeMeta.chargeId ||
                stripeMeta.checkoutSessionId) && (
                <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-2 text-slate-900">
                    <ShieldCheck size={16} className="text-emerald-500" />
                    <span className="font-semibold">Stripe Metadata</span>
                  </div>
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-xs sm:text-sm">
                    {stripeMeta.paymentIntentId && (
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600 mr-2">Payment Intent</dt>
                        <dd className="font-mono text-[11px] sm:text-xs text-slate-800">
                          {stripeMeta.paymentIntentId}
                        </dd>
                      </div>
                    )}
                    {stripeMeta.chargeId && (
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600 mr-2">Charge</dt>
                        <dd className="font-mono text-[11px] sm:text-xs text-slate-800">
                          {stripeMeta.chargeId}
                        </dd>
                      </div>
                    )}
                    {stripeMeta.checkoutSessionId && (
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600 mr-2">Checkout Session</dt>
                        <dd className="font-mono text-[11px] sm:text-xs text-slate-800">
                          {stripeMeta.checkoutSessionId}
                        </dd>
                      </div>
                    )}
                    {stripeMeta.customerId && (
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600 mr-2">Customer</dt>
                        <dd className="font-mono text-[11px] sm:text-xs text-slate-800">
                          {stripeMeta.customerId}
                        </dd>
                      </div>
                    )}
                    {stripeMeta.accountId && (
                      <div className="flex items-center justify-between">
                        <dt className="text-slate-600 mr-2">Connect Account</dt>
                        <dd className="font-mono text-[11px] sm:text-xs text-slate-800">
                          {stripeMeta.accountId}
                        </dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}

              {/* Notes */}
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900 mb-2">
                  Internal Notes
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">
                  {payment.notes || '—'}
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900">Payment Meta</div>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Currency</dt>
                    <dd className="font-medium">{currency}</dd>
                  </div>
                  <div className="flex items-center justify-between">
                    <dt className="text-slate-600">Status</dt>
                    <dd className="font-medium capitalize">
                      {String(payment.status || '—')}
                    </dd>
                  </div>
                  {payment.method && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Method</dt>
                      <dd className="font-medium">
                        {METHOD_LABELS[payment.method] || payment.method}
                      </dd>
                    </div>
                  )}
                  {payment.source && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Source</dt>
                      <dd className="font-medium capitalize">{payment.source}</dd>
                    </div>
                  )}
                  {payment.assignedTo?.name && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Owner</dt>
                      <dd className="font-medium">{payment.assignedTo.name}</dd>
                    </div>
                  )}
                  {payment.createdBy?.name && (
                    <div className="flex items-center justify-between">
                      <dt className="text-slate-600">Recorded By</dt>
                      <dd className="font-medium">{payment.createdBy.name}</dd>
                    </div>
                  )}
                </dl>

                <div className="mt-4 grid grid-cols-1 gap-2">
                  {invoice?._id && (
                    <button
                      onClick={() => navigate(`/invoices/${invoice._id}`)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50 text-sm"
                    >
                      <FileText size={16} /> Open Invoice
                    </button>
                  )}
                  {client?._id && (
                    <button
                      onClick={() => navigate(`/clients/${client._id}`)}
                      className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-slate-700 hover:bg-slate-50 text-sm"
                    >
                      <User2 size={16} /> Open Client
                    </button>
                  )}

                  {!canMutate && (
                    <p className="mt-1 text-[11px] text-slate-400">
                      You have read-only access to payments.
                    </p>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="text-base font-semibold text-slate-900">Activity</div>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  <li>
                    Created:{' '}
                    <span className="font-medium">{fmtDateTimeLocal(payment.createdAt)}</span>
                  </li>
                  {payment.paymentDate && (
                    <li>
                      Payment Date:{' '}
                      <span className="font-medium">{fmtDateOnly(payment.paymentDate)}</span>
                    </li>
                  )}
                  {payment.updatedAt && (
                    <li>
                      Last Updated:{' '}
                      <span className="font-medium">{fmtDateTimeLocal(payment.updatedAt)}</span>
                    </li>
                  )}
                  {invoice?.createdAt && (
                    <li className="text-xs text-slate-500">
                      Linked invoice created {fmtDateTimeLocal(invoice.createdAt)}
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
